/**
 * @fileoverview Supabase authentication service (JWT) for login/logout and auth checks.
 * @module lib/authService
 */

import { supabase, hasCachedSupabaseToken, clearSupabaseAuthTokensSync, storeLastUserId } from './supabaseClient'
import { queryClient } from '@/App'

/**
 * Authentication service providing Supabase login, logout, and auth state checks.
 *
 * @example
 * ```typescript
 * import { authService } from '@/lib/authService';
 * await authService.login('user@example.com', 'password');
 * if (authService.isAuthenticated()) {
 *   // ... do authed work
 * }
 * authService.logout();
 * ```
 */
export const authService = {
  /**
   * Sign in using Supabase email/password.
   * Does not persist any custom tokens; Supabase manages storage.
   *
   * @param email - User email address
   * @param password - User password
   * @returns Promise that resolves when sign-in completes
   * @throws Error when authentication fails
   */
  login: async (email: string, password: string): Promise<void> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  },

  /**
   * Log out the current user. Stores user ID for cache comparison on next login,
   * cancels in-flight queries, clears cached Supabase tokens synchronously,
   * and triggers a non-blocking Supabase signOut.
   */
  logout: (): void => {
    try {
      // Store current user ID before clearing tokens (for cache invalidation on next login)
      storeLastUserId()
      // Cancel all in-flight queries to prevent 401 errors during logout
      queryClient.cancelQueries()
      // Synchronously clear cached tokens to avoid any races
      clearSupabaseAuthTokensSync()
      // Fire-and-forget Supabase sign out
      void supabase.auth.signOut()
    } catch {
      // Ignore errors during best-effort logout
    }
  },

  /**
   * Synchronous check if a Supabase auth token is present in storage.
   * Avoids blocking app bootstrap on getSession().
   *
   * @returns True when an auth token is present in localStorage
   */
  isAuthenticated: (): boolean => {
    return hasCachedSupabaseToken()
  },
}
